import Link from 'components/ui/AppLink';
import UserInterfaceInfoCircleFill from '@mds/icons/Info';
import type { ReactNode as Children } from 'react';
import {
    Box,
    Breadcrumbs,
    Tooltip,
    Typography as BreadcrumbsText,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { Option } from 'components/ui/FormControls';
import React from 'react';

import { ChannelSubscriptionTypeByPayment } from '../../../../shared/types';
import { ManageTemplateInstance } from '../../../ui/ManageTemplate';
import { baseUrls } from '../../../../utils/baseUrls.utils';
import { FormControl } from '../../../../utils/form.utils';
import {
    ChannelSubscribedUser,
    ChannelSubscriberAddEditFragment,
} from '../../../../gql/channels';
import { BulkChannelSubscriptionsInput } from '../../../../gql/subscriptions';

export interface PremiumChannelsSubscribersFormInterface {
    channel: ChannelSubscriberAddEditFragment;
    channelId: string;
    userFormControl: FormControl<Option | undefined>;
    notesFormControl: FormControl<string>;
    fileFormControl: FormControl<File | undefined>;
    returnUrl: string;
    isEdit: boolean;
    isBulk: boolean;
    proceedBulk: (values: BulkChannelSubscriptionsInput) => void;
    usersField: Children;
    notesField: Children;
    currentUser: ChannelSubscribedUser | undefined;
    onDelete: () => void;
}

export const NOTES_CHAR_LIMIT = 250;

export const subscribersMainSx: SxProps<Theme> = {
    flex: 1,
    flexDirection: 'column',
    maxWidth: 1200,
};

export const helperTextSx: SxProps<Theme> = {
    maxWidth: 384,
    mb: 2,
};

const tooltipWrapperSx: SxProps<Theme> = {
    display: 'inline',
    position: 'relative',
    top: 3,
    right: 10,
    maxWidth: 150,
};

export const getPremiumChannelsSubscribersFormInstance = (isBulk: boolean) =>
    isBulk
        ? ManageTemplateInstance.ChannelSubscribers
        : ManageTemplateInstance.ChannelSubscriber;
export const getPremiumChannelsSubscribersFormCreateButtonLabel = (
    isBulk: boolean
) => (isBulk ? 'Subscribe users' : 'Subscribe user');
export const premiumChannelsSubscribersFormUpdateButtonLabel = 'Save';
export const premiumChannelsSubscribersFormDeleteButtonLabel =
    'Remove subscriber';
export const premiumChannelsSubscribersFormDeleteDialogTitle = 'Remove';
export const getPremiumChannelsSubscribersFormBreadCrumbs = (
    channelName: string,
    returnUrl: string,
    isEdit: boolean,
    isBulk: boolean
) => (
    <Breadcrumbs>
        <Link to={baseUrls.publisher.premiumChannels.list}>Channels</Link>
        <Link to={returnUrl}>{channelName}</Link>
        <BreadcrumbsText>
            {isEdit
                ? 'Edit subscriber settings'
                : isBulk
                  ? 'Add subscribers'
                  : 'New subscriber'}
        </BreadcrumbsText>
    </Breadcrumbs>
);

export const isPaidSubscriptionTypeByPayment = (
    subscriptionTypeValue: ChannelSubscriptionTypeByPayment | undefined
) => subscriptionTypeValue === ChannelSubscriptionTypeByPayment.Paid;
export const isFreeSubscriptionTypeByPayment = (
    subscriptionTypeValue: ChannelSubscriptionTypeByPayment | undefined
) => subscriptionTypeValue === ChannelSubscriptionTypeByPayment.Free;
export const isFreeTrialSubscriptionTypeByPayment = (
    subscriptionTypeValue: ChannelSubscriptionTypeByPayment | undefined
) => subscriptionTypeValue === ChannelSubscriptionTypeByPayment.FreeTrial;

export const SubscriptionTypeTooltip = ({
    tooltipText,
}: {
    tooltipText: string;
}) => {
    return (
        <Tooltip title={tooltipText} placement="right">
            <Box component="span" sx={tooltipWrapperSx}>
                <UserInterfaceInfoCircleFill fontSize="small" />
            </Box>
        </Tooltip>
    );
};
